// src/pages/Home/HomePage.js
import React, { useCallback, useMemo, useReducer, useEffect } from "react";
import Layout from "../../components/Layout";
import Hero from "../../components/Hero";
import heroImage from "../../assets/usedcar-new-desk.jpeg";
import heroImageMob from "../../assets/usedcar-new-mob.jpeg";
import {
  Container,
  Grid,
  Alert,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import CarCard from "../../components/Cards/CarCard";
import CarFilter from "../../components/CarFilter/CarFilter";
import { FormattedMessage } from "react-intl";
import { useGetAllCarsQuery } from "../../app/api/Apis";
import { useDispatch, useSelector } from "react-redux";
import CarListSkeleton from "../../components/Loaders/CarListSkeleton";
import NoDataPlaceholder from "../../components/NoDataPlaceholder";
import { refetchToken, showLoader } from "../../utils/globalSlice";
import SeoContent from "../Seo/SeoContent";
import { useLocation } from "react-router-dom";

const PAGE_SIZE = 20;

// `pending` is now owned by US, set synchronously the instant filters
// change or a page is requested — not derived from RTK Query's flags,
// which lag one render behind because they update via RTK Query's own
// internal effect (post-commit), not during the render that changed args.
const initialListState = {
  offset: 0,
  cars: [],
  filterKey: "",
  pending: true, // true until the first page for the current filterKey arrives
};

function carsListReducer(state, action) {
  switch (action.type) {
    case "FILTERS_CHANGED": {
      if (action.filterKey === state.filterKey) return state;
      return {
        offset: 0,
        cars: [],
        filterKey: action.filterKey,
        pending: true,
      };
    }
    case "PAGE_LOADED": {
      // drop stale responses from a filter/offset combo that's no longer current
      if (
        action.filterKey !== state.filterKey ||
        action.offset !== state.offset
      ) {
        return state;
      }
      return {
        ...state,
        cars:
          action.offset === 0
            ? action.content
            : [...state.cars, ...action.content],
        pending: false,
      };
    }
    case "LOAD_MORE":
      return { ...state, offset: state.offset + PAGE_SIZE, pending: true };
    default:
      return state;
  }
}

const HomePage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const featuredBrand = params.get("brand");
  const featuredModel = params.get("model");

  const dispatch = useDispatch();

  const tokenError = useSelector((state) => state.global.apiError);
  const brand = useSelector((state) => state.carFilter.selectedBrand);
  const model = useSelector((state) => state.carFilter.selectedModel);
  const year = useSelector((state) => state.carFilter.selectedYear);
  const isMob = useSelector((state) => state.global.isMobile);

  const filterKey = `${brand}|${model}|${year}`;

  const [listState, listDispatch] = useReducer(
    carsListReducer,
    initialListState
  );

  // Synchronous reset during render — guarantees `pending: true` is set
  // in the SAME render the filter changed, so there's no gap where
  // hasCars=false AND pending=false simultaneously.
  if (listState.filterKey !== filterKey) {
    listDispatch({ type: "FILTERS_CHANGED", filterKey });
  }

  const payload = useMemo(
    () => ({
      payloadData: {
        brandId: brand === "" ? null : brand,
        modelId: model === "" ? null : model,
        yearOfManufacture: year === "" ? null : year,
        bodyTypeId: null,
        carStatusId: 1,
      },
      limit: PAGE_SIZE,
      offset: listState.offset,
    }),
    [brand, model, year, listState.offset]
  );

  const {
    isLoading: carsLoading,
    isError: carsError,
    data: carsList,
    refetch,
  } = useGetAllCarsQuery(payload, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!carsList?.content) return;
    listDispatch({
      type: "PAGE_LOADED",
      filterKey,
      offset: listState.offset,
      content: carsList.content,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carsList]);

  useEffect(() => {
    if (carsLoading) {
      dispatch(showLoader(true));
      return () => dispatch(showLoader(false));
    }
  }, [carsLoading, dispatch]);

  const handleRetry = useCallback(() => {
    if (tokenError !== "") {
      dispatch(refetchToken());
    } else {
      refetch();
    }
  }, [tokenError, dispatch, refetch]);

  const handleLoadMore = useCallback(() => {
    listDispatch({ type: "LOAD_MORE" });
  }, []);

  const hasMore = carsList?.content?.length === PAGE_SIZE;
  const hasCars = listState.cars.length > 0;

  // pending is now the single source of truth for "we're waiting on a
  // request for the currently-selected filters" — no reliance on
  // RTK Query's isLoading/isFetching timing at all.
  const isInitialLoad = listState.pending && !hasCars;
  const isLoadingMore = listState.pending && listState.offset > 0 && hasCars;

  return (
    <Layout>
      <SeoContent
        title="Fazaa Used Cars"
        image={`${window?.location?.origin}${heroImageMob}`}
        description="Explore a wide selection of quality used cars at unbeatable prices. Browse through our extensive inventory, find your perfect match, and enjoy a hassle-free buying experience with trusted dealers. Start your journey today!"
        url="https://usedcars.fazaa.ae/"
        keywords="Used cars for sale, Affordable used cars, Certified used cars, Used car deals, Buy used cars online, Used cars near me, Best used cars, Used cars with warranty"
      />
      <Hero
        image={isMob ? heroImageMob : heroImage}
        text={<FormattedMessage id="app.heroText" />}
        scrollPosition="650"
      />
      <Container maxWidth="lg" sx={{ my: "2rem", flexGrow: 1, p: 2 }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <CarFilter
              presetBrand={featuredBrand}
              presetModel={featuredModel}
            />
          </Grid>

          {(carsError || tokenError) && (
            <Grid item xs={12}>
              <Alert
                severity="error"
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={handleRetry}
                  >
                    Retry
                  </Button>
                }
              >
                Something went wrong white loading the cars!!
              </Alert>
            </Grid>
          )}

          {isInitialLoad ? (
            [...Array(10).keys()].map((item, index) => (
              <CarListSkeleton key={index} />
            ))
          ) : hasCars ? (
            listState.cars.map((car) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                sx={{ display: "flex" }}
                key={car.usedCarUUID}
              >
                <CarCard data={car} />
              </Grid>
            ))
          ) : (
            <NoDataPlaceholder message="No cars available with the current search. Try a different one!!" />
          )}
        </Grid>

        {!isInitialLoad && hasMore && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              startIcon={isLoadingMore ? <CircularProgress size={16} /> : null}
            >
              {isLoadingMore ? "Loading..." : "Load More"}
            </Button>
          </Box>
        )}
      </Container>
    </Layout>
  );
};

export default HomePage;